import { useCallback, useRef, useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "../ui/modal.tsx";
import { useToast } from "../../hooks/useToast.ts";
import { setScheduledExport } from "../../api/endpoints.ts";
import { ScheduleExportForm, type ScheduleExportFormHandle, type ScheduleGridColumns } from "./scheduleExportForm.tsx";
import type { InputParam, SortOrder } from "../../types/grid.ts";
import type { ScheduledExport } from "../../api/scheduledExport.ts";
import styles from "./dataGrid.module.scss";

interface ScheduleExportDialogProps {
  name: string;
  filters: InputParam[];
  paramFilters: InputParam[];
  sorts: SortOrder[];
  /** Column-chooser context from the opening grid (drives the Columns checklist). */
  gridColumns?: ScheduleGridColumns;
  onClose: () => void;
}

export function ScheduleExportDialog({ name, filters, paramFilters, sorts, gridColumns, onClose }: ScheduleExportDialogProps) {
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const formRef = useRef<ScheduleExportFormHandle>(null);

  const initialFilters = [...filters, ...paramFilters];

  const handleSave = useCallback(async (item: ScheduledExport) => {
    if (!item.Title.trim()) {
      toast("Title is required", "error");
      return;
    }
    setSaving(true);
    try {
      const result = await setScheduledExport(item);
      if (!result.bError && result.oData) {
        toast("Scheduled export saved successfully", "success");
        onClose();
      } else {
        toast(result.sMessage ?? "Failed to save scheduled export", "error");
      }
    } catch {
      toast("Failed to save scheduled export", "error");
    } finally {
      setSaving(false);
    }
  }, [toast, onClose]);

  return (
    <Modal open onClose={onClose} wide>
      <ModalHeader title={`Schedule Export: ${name}`} onClose={onClose} />
      <ModalBody>
        <ScheduleExportForm
          ref={formRef}
          item={null}
          fixedReportName={name}
          initialGridFilters={initialFilters}
          initialGridSorts={sorts}
          gridColumns={gridColumns}
          onSave={handleSave}
          saving={saving}
        />
      </ModalBody>
      <ModalFooter>
        <button className={styles.toolbarBtn} onClick={onClose} disabled={saving}>Cancel</button>
        <button className={styles.toolbarBtnPrimary} onClick={() => formRef.current?.submit()} disabled={saving}>
          {saving ? "Saving..." : "Save"}
        </button>
      </ModalFooter>
    </Modal>
  );
}
